import { Building04, ChevronDown, Plus } from "@untitledui/icons";
import {
	type ReactElement,
	type ReactNode,
	useCallback,
	useState,
} from "react";
import { Button as AriaButton, type Key } from "react-aria-components";
import type { BadgeColors } from "@/components/base/badges/badge-types";
import {
	Badge,
	BadgeWithDot,
	BadgeWithIcon,
} from "@/components/base/badges/badges";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Label } from "@/components/base/label/label";
import { cx } from "@/utils/cx";
import { DisplayFieldShell } from "./DisplayFieldShell";
import {
	type DisplayFieldBaseProps,
	DisplayFieldContent,
	DisplayFieldFeedback,
	type DisplayFieldIcon,
	DisplayFieldState,
	displayFieldTrailingIconToneClass,
	resolveDisplayContent,
} from "./display-field.shared";

export const DisplaySelectValueType = {
	Badge: "badge",
	Text: "text",
	Label: "label",
	/** Renders `value` as-is (e.g. multi-line custom summary). */
	Custom: "custom",
} as const;

export type DisplaySelectValueType =
	(typeof DisplaySelectValueType)[keyof typeof DisplaySelectValueType];

export const DisplaySelectBadgeVariant = {
	Pill: "pill",
	Dot: "dot",
} as const;

export const DisplaySelectEmptyTrailing = {
	Edit: "edit",
	Add: "add",
} as const;

export type DisplaySelectEmptyTrailing =
	(typeof DisplaySelectEmptyTrailing)[keyof typeof DisplaySelectEmptyTrailing];

export type DisplaySelectBadgeVariant =
	(typeof DisplaySelectBadgeVariant)[keyof typeof DisplaySelectBadgeVariant];

export interface DisplaySelectOption {
	id: string;
	label: string;
	testId?: string;
}

/** Sentinel id for the disabled placeholder row when `options` is empty. */
const EMPTY_MENU_OPTION_ID = "__display-select-empty__";

export interface DisplaySelectProps extends DisplayFieldBaseProps {
	/** Placeholder when empty (shown as the empty-state label text). */
	placeholder?: string;
	/** Displayed value when populated (used for text / badge / label modes). */
	value?: ReactNode;
	/**
	 * How the populated value is rendered.
	 * @default "text"
	 */
	valueType?: DisplaySelectValueType;
	/** Icon inside Badge / Label value slots. Defaults to Building04. */
	valueIcon?: DisplayFieldIcon;
	/** Show the value-slot icon. @default true for badge/label */
	showValueIcon?: boolean;
	/**
	 * Badge color when `valueType` is badge.
	 * @default "gray"
	 */
	badgeColor?: BadgeColors;
	/**
	 * Badge chrome when `valueType` is badge and `showValueIcon` is false.
	 * `"pill"` uses solid `Badge`; `"dot"` uses Destiny `BadgeWithDot`.
	 * Ignored when `showValueIcon` is true (`BadgeWithIcon`).
	 * @default "pill"
	 */
	badgeVariant?: DisplaySelectBadgeVariant;
	/**
	 * Menu options. When provided, DisplaySelect owns a DropdownMenu and opens
	 * it on the first click (Figma Active).
	 */
	options?: DisplaySelectOption[];
	/** Currently selected option id (menu selection). */
	selectedKey?: string | null;
	/** Called when the user picks an option from the menu. */
	onSelectionChange?: (key: string) => void;
	/** Controlled menu open state. */
	isMenuOpen?: boolean;
	/** Called when the menu open state changes. */
	onMenuOpenChange?: (isOpen: boolean) => void;
	/**
	 * Trailing icon when empty. `"add"` shows Plus (Figma set-location empty).
	 * @default "edit"
	 */
	emptyTrailing?: DisplaySelectEmptyTrailing;
	/** Called when the empty add trailing is activated. */
	onEmptyAction?: () => void;
}

/**
 * Underline select trigger for label + chosen value in drawers / detail panels.
 * With `options`, opens DropdownMenu on Active in one click.
 *
 * @see Figma DisplaySelect 3355:26567
 */
export function DisplaySelect({
	label,
	placeholder,
	value,
	valueType = DisplaySelectValueType.Text,
	valueIcon: ValueIcon = Building04,
	showValueIcon = true,
	badgeColor = "gray",
	badgeVariant = DisplaySelectBadgeVariant.Pill,
	state = DisplayFieldState.Enabled,
	content: contentProp,
	leadingIcon,
	showLeadingIcon,
	hintText,
	showHintText,
	feedback = DisplayFieldFeedback.Idle,
	showEdit,
	onEdit,
	editAriaLabel,
	options,
	selectedKey,
	onSelectionChange,
	isMenuOpen: isMenuOpenProp,
	onMenuOpenChange,
	emptyTrailing = DisplaySelectEmptyTrailing.Edit,
	onEmptyAction,
	className,
	"data-testid": dataTestId = "display-select",
}: DisplaySelectProps): ReactElement {
	const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
	const isMenuOpen = isMenuOpenProp ?? uncontrolledOpen;
	const setMenuOpen = onMenuOpenChange ?? setUncontrolledOpen;

	const content = resolveDisplayContent(contentProp, value);
	const isPopulated = content === DisplayFieldContent.Populated;
	const isDisabled = state === DisplayFieldState.Disabled;
	const hasOptions = options !== undefined;
	const canOpenMenu =
		hasOptions &&
		(showEdit ?? true) &&
		!isDisabled &&
		feedback === DisplayFieldFeedback.Idle;
	const isActive =
		isMenuOpen || (!hasOptions && state === DisplayFieldState.Active);
	const resolvedState =
		isMenuOpen && state !== DisplayFieldState.Error
			? DisplayFieldState.Active
			: state;
	const emptyLabel = placeholder ?? label;
	const menuAriaLabel = editAriaLabel ?? `Open ${label} menu`;
	const trailingIconTone = displayFieldTrailingIconToneClass({
		isDisabled,
		isEmphasized: isActive || resolvedState === DisplayFieldState.Hovered,
	});
	const showAddTrailing =
		emptyTrailing === DisplaySelectEmptyTrailing.Add &&
		!isPopulated &&
		!isActive &&
		!isDisabled &&
		feedback === DisplayFieldFeedback.Idle;

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (open && !canOpenMenu) return;
			if (!open && !hasOptions) return;
			setMenuOpen(open);
			if (open) onEdit?.();
		},
		[canOpenMenu, hasOptions, onEdit, setMenuOpen],
	);

	const handleAction = useCallback(
		(key: Key) => {
			const next = String(key);
			if (next === EMPTY_MENU_OPTION_ID) {
				setMenuOpen(false);
				return;
			}
			onSelectionChange?.(next);
			setMenuOpen(false);
		},
		[onSelectionChange, setMenuOpen],
	);

	const menuOptions = options ?? [];
	const emptyOptionLabel = placeholder?.trim() || "—";

	const valueSlot =
		isPopulated || isActive ? (
			<div className="flex h-6 w-full min-w-0 items-center gap-2.5 overflow-hidden">
				{renderSelectValue({
					value,
					valueType,
					ValueIcon,
					showValueIcon,
					badgeColor,
					badgeVariant,
				})}
			</div>
		) : null;

	const shell = (
		<DisplayFieldShell
			label={isPopulated || isActive ? label : emptyLabel}
			state={resolvedState}
			isPopulated={isPopulated || isActive}
			leadingIcon={leadingIcon}
			showLeadingIcon={showLeadingIcon}
			hintText={hintText}
			showHintText={showHintText}
			feedback={feedback}
			showEdit={
				showAddTrailing
					? false
					: hasOptions
						? canOpenMenu && !isActive
						: (showEdit ?? true) && !isDisabled && !isActive
			}
			onEdit={hasOptions ? undefined : onEdit}
			editAriaLabel={editAriaLabel}
			isTriggerChild={canOpenMenu && !isActive}
			className={hasOptions ? undefined : className}
			data-testid={dataTestId}
			trailing={
				isActive ? (
					<span
						className={cx(
							"flex size-[21px] items-center justify-center",
							trailingIconTone,
						)}
						aria-hidden
					>
						<ChevronDown className="size-5" />
					</span>
				) : showAddTrailing ? (
					onEmptyAction ? (
						<button
							type="button"
							aria-label={emptyLabel}
							className={cx(
								"flex size-[21px] cursor-pointer items-center justify-center outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2",
								trailingIconTone,
							)}
							onClick={onEmptyAction}
						>
							<Plus className="size-5" />
						</button>
					) : (
						<span
							className={cx(
								"flex size-[21px] items-center justify-center",
								trailingIconTone,
							)}
							aria-hidden
						>
							<Plus className="size-5" />
						</span>
					)
				) : undefined
			}
		>
			{valueSlot}
		</DisplayFieldShell>
	);

	if (!hasOptions) {
		return shell;
	}

	const selectedKeys =
		selectedKey != null && selectedKey !== ""
			? new Set<Key>([selectedKey])
			: new Set<Key>();

	const isTriggerDisabled = !canOpenMenu && !isMenuOpen;

	return (
		<div className={cx("w-full", className)}>
			<Dropdown.Root isOpen={isMenuOpen} onOpenChange={handleOpenChange}>
				<AriaButton
					aria-label={menuAriaLabel}
					aria-expanded={isMenuOpen}
					aria-invalid={state === DisplayFieldState.Error || undefined}
					aria-describedby={
						(showHintText ?? state === DisplayFieldState.Error) && hintText
							? `${dataTestId}-hint`
							: undefined
					}
					isDisabled={isTriggerDisabled}
					className={cx(
						"w-full border-0 bg-transparent p-0 text-left outline-focus-ring",
						"focus-visible:outline-2 focus-visible:outline-offset-2",
						isTriggerDisabled && "cursor-not-allowed",
					)}
				>
					{shell}
				</AriaButton>
				<Dropdown.Popover
					placement="bottom start"
					className="w-(--trigger-width)"
				>
					<Dropdown.Menu
						aria-label={label}
						selectionMode="single"
						selectedKeys={selectedKeys}
						onAction={handleAction}
						disallowEmptySelection={menuOptions.length > 0}
						disabledKeys={
							menuOptions.length === 0 ? [EMPTY_MENU_OPTION_ID] : undefined
						}
					>
						{menuOptions.length > 0 ? (
							menuOptions.map((option) => (
								<Dropdown.Item
									key={option.id}
									id={option.id}
									label={option.label}
									textValue={option.label}
									data-testid={option.testId}
								/>
							))
						) : (
							<Dropdown.Item
								id={EMPTY_MENU_OPTION_ID}
								label={emptyOptionLabel}
								textValue={emptyOptionLabel}
								isDisabled
							/>
						)}
					</Dropdown.Menu>
				</Dropdown.Popover>
			</Dropdown.Root>
		</div>
	);
}

function renderSelectValue(options: {
	value: ReactNode;
	valueType: DisplaySelectValueType;
	ValueIcon: DisplayFieldIcon;
	showValueIcon: boolean;
	badgeColor: BadgeColors;
	badgeVariant: DisplaySelectBadgeVariant;
}): ReactElement {
	const {
		value,
		valueType,
		ValueIcon,
		showValueIcon,
		badgeColor,
		badgeVariant,
	} = options;

	if (valueType === DisplaySelectValueType.Custom) {
		return <>{value}</>;
	}

	if (valueType === DisplaySelectValueType.Badge) {
		if (showValueIcon) {
			return (
				<BadgeWithIcon color={badgeColor} size="md" iconLeading={ValueIcon}>
					{value}
				</BadgeWithIcon>
			);
		}
		if (badgeVariant === DisplaySelectBadgeVariant.Dot) {
			return (
				<BadgeWithDot color={badgeColor} size="md">
					{value}
				</BadgeWithDot>
			);
		}
		return (
			<Badge color={badgeColor} size="md">
				{value}
			</Badge>
		);
	}

	if (valueType === DisplaySelectValueType.Label) {
		return (
			<Label size="md" type="dark" icon={ValueIcon} showIcon={showValueIcon}>
				{value}
			</Label>
		);
	}

	return (
		<p className="w-full truncate font-display text-sm font-extrabold text-primary">
			{value}
		</p>
	);
}
